import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import GearMark from "@/components/GearMark";
import { EASE } from "@/components/Reveal";

const CHIPS = [
  { title: "AWS Marketplace", sub: "listing · sync live", dot: "vermillion", cls: "left-[3%] top-[8%]", depth: 34 },
  { title: "Partner CRM", sub: "pipeline · in agreement", dot: "bluespark", cls: "right-[1%] top-[25%]", depth: 46 },
  { title: "Co-sell Signal", sub: "new upsell detected", dot: "vermillion", cls: "left-[5%] bottom-[17%]", depth: 40 },
  { title: "Private Offer", sub: "draft ready for review", dot: "bluespark", cls: "right-[3%] bottom-[5%]", depth: 28 },
];

function Chip({ chip, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.75 + index * 0.14, ease: EASE }}
      style={{ transform: `translateZ(${chip.depth}px)` }}
      className={`absolute z-10 ${chip.cls}`}
    >
      <div className="flex items-start gap-2.5 rounded-xl border border-line bg-white/90 px-4 py-3 shadow-[0_18px_40px_-24px_rgba(31,28,25,0.35)] backdrop-blur-md">
        <span className="relative mt-1 flex h-2 w-2 shrink-0">
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${
              chip.dot === "vermillion" ? "bg-vermillion" : "bg-bluespark"
            }`}
          />
          <span
            className={`relative inline-flex h-2 w-2 rounded-full ${
              chip.dot === "vermillion" ? "bg-vermillion" : "bg-bluespark"
            }`}
          />
        </span>
        <span>
          <span className="block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-ink">
            {chip.title}
          </span>
          <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.14em] text-inksoft">
            {chip.sub}
          </span>
        </span>
      </div>
    </motion.div>
  );
}

export default function HeroVisual() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yRings = useTransform(scrollYProgress, [0, 1], [20, -20]);

  return (
    <div ref={ref} data-testid="hero-visual" className="relative mx-auto w-full max-w-[470px]">
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
        className="relative aspect-square overflow-hidden rounded-[2rem] border border-line bg-white/70 shadow-[0_40px_90px_-50px_rgba(31,28,25,0.35)] backdrop-blur-sm"
      >
        <span className="pointer-events-none absolute left-4 top-4 z-10 font-mono text-sm text-line">+</span>
        <span className="pointer-events-none absolute right-4 top-4 z-10 font-mono text-sm text-line">+</span>
        <span className="pointer-events-none absolute bottom-4 left-4 z-10 font-mono text-sm text-line">+</span>
        <span className="pointer-events-none absolute bottom-4 right-4 z-10 font-mono text-sm text-line">+</span>

        <motion.svg
          viewBox="0 0 600 600"
          className="absolute inset-0 h-full w-full"
          style={{ y: yRings }}
          initial="hidden"
          animate="visible"
        >
          <defs>
            <pattern id="bp" width="30" height="30" patternUnits="userSpaceOnUse">
              <path d="M30 0H0V30" fill="none" stroke="#E8E4DD" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="600" height="600" fill="url(#bp)" opacity="0.55" />

          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.5 }}
          >
            <line x1="300" y1="0" x2="300" y2="600" stroke="#A9C7DD" strokeWidth="1" strokeDasharray="1 9" opacity="0.5" />
            <line x1="0" y1="300" x2="600" y2="300" stroke="#A9C7DD" strokeWidth="1" strokeDasharray="1 9" opacity="0.5" />

            <circle cx="300" cy="300" r="118" fill="none" stroke="#E8E4DD" strokeWidth="1.2" />
            <circle cx="300" cy="300" r="182" fill="none" stroke="#A9C7DD" strokeWidth="1" strokeDasharray="4 9" opacity="0.8" />
            <circle cx="300" cy="300" r="246" fill="none" stroke="#E8E4DD" strokeWidth="1" strokeDasharray="2 12" />

            <line x1="300" y1="300" x2="118" y2="96" stroke="#A9C7DD" strokeWidth="1.2" strokeDasharray="3 6" />
            <line x1="300" y1="300" x2="484" y2="168" stroke="#A9C7DD" strokeWidth="1.2" strokeDasharray="3 6" />
            <line x1="300" y1="300" x2="122" y2="452" stroke="#A9C7DD" strokeWidth="1.2" strokeDasharray="3 6" />
            <line x1="300" y1="300" x2="482" y2="540" stroke="#A9C7DD" strokeWidth="1.2" strokeDasharray="3 6" />

            <circle r="3.5" fill="#D6350A">
              <animateMotion dur="5s" repeatCount="indefinite" path="M300 300 L118 96" />
            </circle>
            <circle r="3" fill="#38BDF8">
              <animateMotion dur="6.5s" begin="1.4s" repeatCount="indefinite" path="M300 300 L482 540" />
            </circle>
            <circle r="2.5" fill="#D6350A" opacity="0.7">
              <animateMotion dur="7.5s" begin="2.6s" repeatCount="indefinite" path="M300 300 L122 452" />
            </circle>
          </motion.g>
        </motion.svg>

        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.7, rotate: -90 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.3, delay: 0.45, ease: EASE }}
            className="flex items-center justify-center"
          >
            <GearMark
              size={150}
              className="animate-spin-slower drop-shadow-[0_20px_45px_rgba(214,53,10,0.4)]"
            />
          </motion.div>
        </div>

        {CHIPS.map((chip, i) => (
          <Chip key={chip.title} chip={chip} index={i} />
        ))}
      </motion.div>

      <div className="mt-5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-inksoft/70">
        <span>Fig. 01 — The autonomous revenue engine</span>
        <span className="hidden sm:inline">TRANSACTR.GEAR / v1</span>
      </div>
    </div>
  );
}
